import React, { useEffect, useState } from "react";
import { api, formatApiError } from "../../lib/api";
import { useLang } from "../../contexts/LangContext";
import { t } from "../../i18n/translations";
import { toast } from "sonner";
import { Bell, RefreshCw, Send, Smartphone, ExternalLink, KeyRound, Eye, EyeOff } from "lucide-react";

function randomTopic() {
  // Random readable suffix (e.g. ayurveda-aargau-x4k9m2)
  const chars = "abcdefghijkmnpqrstuvwxyz23456789";
  let s = "";
  for (let i = 0; i < 8; i++) s += chars[Math.floor(Math.random() * chars.length)];
  return `ayurveda-aargau-${s}`;
}

export default function AdminSettings() {
  const { lang } = useLang();
  const [s, setS] = useState(null);
  const load = async () => { const { data } = await api.get("/admin/settings"); setS(data || {}); };
  useEffect(() => { load(); }, []);

  if (!s) return <div className="p-10">…</div>;

  const save = async () => {
    try {
      await api.put("/admin/settings", {
        business_name: s.business_name || "Ayurveda Aargau",
        address: s.address || "",
        phone: s.phone || "",
        email: s.email || "",
        booking_email: s.booking_email || "",
        working_hours: s.working_hours || {},
        blocked_dates: s.blocked_dates || [],
        slot_step_minutes: parseInt(s.slot_step_minutes) || 30,
        buffer_minutes: parseInt(s.buffer_minutes) || 30,
        notification_topic: (s.notification_topic || "").trim(),
        voucher_amounts: s.voucher_amounts || [60, 100, 120, 200],
        intro_de: s.intro_de || "", intro_fr: s.intro_fr || "",
        intro_it: s.intro_it || "", intro_en: s.intro_en || "",
        package_title_de: s.package_title_de || "",
        package_title_en: s.package_title_en || "",
        package_body_de: s.package_body_de || "",
        package_body_en: s.package_body_en || "",
        package_cta_de: s.package_cta_de || "",
        package_cta_en: s.package_cta_en || "",
      });
      toast.success("Gespeichert ✓");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const testPush = async () => {
    try {
      await api.post("/admin/notifications/test");
      toast.success("Test-Push gesendet — schau auf dein Handy!");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const generateTopic = () => setS({ ...s, notification_topic: randomTopic() });

  const topic = (s.notification_topic || "").trim();
  const subscribeUrl = topic ? `https://ntfy.sh/${encodeURIComponent(topic)}` : "";
  const qrUrl = topic ? `https://api.qrserver.com/v1/create-qr-code/?size=240x240&margin=8&data=${encodeURIComponent(subscribeUrl)}` : "";

  return (
    <div className="p-5 sm:p-8 lg:p-10 max-w-3xl" data-testid="admin-settings-page">
      <h1 className="font-display text-3xl sm:text-4xl text-ink">{t(lang, "admin.settings")}</h1>

      {/* Business info */}
      <section className="mt-6 bg-white border border-border rounded-md p-5 sm:p-6 space-y-4">
        <h2 className="font-display text-xl text-ink">Praxis-Informationen</h2>
        <Field label="Praxis-Name" value={s.business_name} onChange={(v) => setS({...s, business_name: v})} testid="set-name" />
        <Field label={t(lang, "contact.address")} value={s.address} onChange={(v) => setS({...s, address: v})} testid="set-addr" />
        <Field label={t(lang, "contact.phone")} value={s.phone} onChange={(v) => setS({...s, phone: v})} testid="set-phone" />
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Allgemeine E-Mail" value={s.email} onChange={(v) => setS({...s, email: v})} testid="set-email" />
          <Field label="Buchungs-E-Mail" value={s.booking_email} onChange={(v) => setS({...s, booking_email: v})} testid="set-booking-email" />
        </div>
        <div className="grid sm:grid-cols-2 gap-4 pt-3 border-t border-border">
          {["de","fr","it","en"].map((l) => (
            <Field key={l} label={`Intro (${l.toUpperCase()})`} value={s[`intro_${l}`] || ""} onChange={(v) => setS({...s, [`intro_${l}`]: v})} textarea testid={`set-intro-${l}`} />
          ))}
        </div>
      </section>

      {/* Package/Multi-Booking offer (on /massagen) */}
      <section className="mt-6 bg-white border border-border rounded-md p-5 sm:p-6 space-y-4" data-testid="package-offer-settings">
        <div>
          <h2 className="font-display text-xl text-ink">Mehrfach-Buchungs-Box (auf /massagen)</h2>
          <p className="text-sm text-ink-muted mt-1">
            Das ist die grüne WhatsApp-Box auf der Massagen-Seite. Änderungen sind nach Speichern sofort live.
          </p>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field
            label="Titel (DE)"
            value={s.package_title_de || ""}
            onChange={(v) => setS({ ...s, package_title_de: v })}
            testid="set-pkg-title-de"
          />
          <Field
            label="Titel (EN)"
            value={s.package_title_en || ""}
            onChange={(v) => setS({ ...s, package_title_en: v })}
            testid="set-pkg-title-en"
          />
          <Field
            label="Text (DE) – z. B. „5 % Rabatt ab 3 Terminen"
            value={s.package_body_de || ""}
            onChange={(v) => setS({ ...s, package_body_de: v })}
            textarea
            testid="set-pkg-body-de"
          />
          <Field
            label="Text (EN)"
            value={s.package_body_en || ""}
            onChange={(v) => setS({ ...s, package_body_en: v })}
            textarea
            testid="set-pkg-body-en"
          />
          <Field
            label="Button-Text (DE)"
            value={s.package_cta_de || ""}
            onChange={(v) => setS({ ...s, package_cta_de: v })}
            testid="set-pkg-cta-de"
          />
          <Field
            label="Button-Text (EN)"
            value={s.package_cta_en || ""}
            onChange={(v) => setS({ ...s, package_cta_en: v })}
            testid="set-pkg-cta-en"
          />
        </div>
        <p className="text-[11px] text-ink-muted">
          Tipp: Leer lassen setzt den Standard-Text zurück (aktuell „10–15 % Rabatt ab 3 Terminen").
        </p>
      </section>

      {/* Push notifications */}
      <section className="mt-6 bg-white border border-border rounded-md p-5 sm:p-6" data-testid="push-settings">
        <div className="flex items-center gap-3 mb-2">
          <Bell className="w-5 h-5 text-terracotta" strokeWidth={1.5} />
          <h2 className="font-display text-xl text-ink">Push-Benachrichtigungen</h2>
        </div>
        <p className="text-sm text-ink-muted">
          Erhalte auf deinem Handy sofort eine Benachrichtigung bei <strong>neuen Buchungen, Wartelisten-Einträgen, Gutschein-Anfragen und Nachrichten</strong>.
        </p>

        <ol className="mt-4 text-sm text-ink space-y-2 list-decimal pl-5">
          <li>Installiere die kostenlose App <strong>„ntfy"</strong> aus dem App Store oder Google Play.</li>
          <li>Klicke unten auf <em>„Topic generieren"</em> und dann auf <em>„Speichern"</em>.</li>
          <li>Öffne in der ntfy-App das <strong>+ Symbol</strong> → „Subscribe to topic" → scanne den QR-Code unten oder gib das Topic manuell ein.</li>
          <li>Tippe auf <em>„Test-Push senden"</em> — du solltest auf deinem Handy sofort eine Benachrichtigung erhalten.</li>
        </ol>

        <div className="mt-5 flex items-center gap-2">
          <input
            type="text"
            value={s.notification_topic || ""}
            onChange={(e) => setS({...s, notification_topic: e.target.value.replace(/[^a-zA-Z0-9_-]/g, "-")})}
            placeholder="z. B. ayurveda-aargau-xxxx"
            className="flex-1 px-3 py-2.5 border border-border rounded-md font-mono text-sm"
            data-testid="push-topic-input"
          />
          <button type="button" onClick={generateTopic} className="inline-flex items-center gap-1.5 px-3 py-2.5 border border-border rounded-md text-sm hover:bg-muted" data-testid="push-generate">
            <RefreshCw className="w-4 h-4" strokeWidth={1.5} /> Topic generieren
          </button>
        </div>
        <p className="mt-2 text-[11px] text-ink-muted">Wähle einen eindeutigen, schwer zu erratenden Namen — sonst könnte jemand anderes Benachrichtigungen mitlesen.</p>

        {topic && (
          <div className="mt-5 grid sm:grid-cols-[240px_1fr] gap-5 p-4 bg-sand-soft border border-border rounded-md">
            <div className="bg-white p-2 rounded-md inline-block">
              <img src={qrUrl} alt="QR-Code zum Abonnieren" className="block w-[240px] h-[240px]" data-testid="push-qr" />
            </div>
            <div className="flex flex-col justify-center text-sm">
              <div className="flex items-center gap-2 text-ink mb-2"><Smartphone className="w-4 h-4 text-terracotta" strokeWidth={1.5} /> So abonnierst du:</div>
              <ol className="text-ink-muted space-y-1 list-decimal pl-5">
                <li>Öffne die ntfy-App auf deinem Handy</li>
                <li>Tippe auf <strong>+ Subscribe to topic</strong></li>
                <li>Wähle <strong>„Use QR code"</strong> und scanne nebenan</li>
              </ol>
              <a href={subscribeUrl} target="_blank" rel="noreferrer" className="mt-3 inline-flex items-center gap-1.5 text-terracotta text-xs hover:underline" data-testid="push-open-url">
                <ExternalLink className="w-3.5 h-3.5" strokeWidth={1.5} /> {subscribeUrl}
              </a>
              <button type="button" onClick={testPush} className="mt-4 self-start inline-flex items-center gap-2 px-4 py-2 bg-terracotta text-white rounded-full text-sm hover:bg-terracotta-hover" data-testid="push-test">
                <Send className="w-4 h-4" strokeWidth={1.5} /> Test-Push senden
              </button>
            </div>
          </div>
        )}

        <div className="mt-4 text-[11px] text-ink-muted flex gap-2 flex-wrap">
          <a href="https://apps.apple.com/de/app/ntfy/id1625396347" target="_blank" rel="noreferrer" className="hover:text-terracotta">📱 App Store (iOS)</a>
          <span>·</span>
          <a href="https://play.google.com/store/apps/details?id=io.heckel.ntfy" target="_blank" rel="noreferrer" className="hover:text-terracotta">🤖 Google Play (Android)</a>
        </div>
      </section>

      <button onClick={save} className="mt-6 w-full sm:w-auto px-6 py-3 bg-terracotta text-white rounded-full hover:bg-terracotta-hover" data-testid="set-save">{t(lang, "admin.save")}</button>

      <PasswordChangeSection />
    </div>
  );
}

function PasswordChangeSection() {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (next.length < 8) { toast.error("Neues Passwort muss mindestens 8 Zeichen lang sein"); return; }
    if (next !== confirm) { toast.error("Die beiden neuen Passwörter stimmen nicht überein"); return; }
    setSaving(true);
    try {
      await api.put("/admin/auth/change-password", { current_password: current, new_password: next });
      toast.success("Passwort erfolgreich geändert");
      setCurrent(""); setNext(""); setConfirm("");
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Passwort konnte nicht geändert werden");
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={submit} className="mt-10 pt-8 border-t border-border max-w-xl" data-testid="password-change-section">
      <h2 className="font-display text-2xl text-ink flex items-center gap-2">
        <KeyRound className="w-5 h-5 text-terracotta" strokeWidth={1.5} /> Passwort ändern
      </h2>
      <p className="text-sm text-ink-muted mt-2 mb-5">Mindestens 8 Zeichen. Verwende Buchstaben, Zahlen und Sonderzeichen.</p>
      <div className="space-y-4">
        <div className="relative">
          <label className="block">
            <span className="text-xs uppercase tracking-widest text-ink-muted">Aktuelles Passwort</span>
            <input type={show ? "text" : "password"} value={current} onChange={(e) => setCurrent(e.target.value)} required
              data-testid="pw-current"
              className="mt-2 w-full px-3 py-2.5 border border-border rounded-md pr-12" />
          </label>
          <button type="button" onClick={() => setShow((v) => !v)} aria-label="Anzeigen"
            className="absolute right-2 top-9 p-2 text-ink-muted hover:text-ink">
            {show ? <EyeOff className="w-4 h-4" strokeWidth={1.5}/> : <Eye className="w-4 h-4" strokeWidth={1.5}/>}
          </button>
        </div>
        <label className="block">
          <span className="text-xs uppercase tracking-widest text-ink-muted">Neues Passwort</span>
          <input type={show ? "text" : "password"} value={next} onChange={(e) => setNext(e.target.value)} required minLength={8}
            data-testid="pw-new"
            className="mt-2 w-full px-3 py-2.5 border border-border rounded-md" />
        </label>
        <label className="block">
          <span className="text-xs uppercase tracking-widest text-ink-muted">Neues Passwort bestätigen</span>
          <input type={show ? "text" : "password"} value={confirm} onChange={(e) => setConfirm(e.target.value)} required minLength={8}
            data-testid="pw-confirm"
            className="mt-2 w-full px-3 py-2.5 border border-border rounded-md" />
        </label>
        <button type="submit" disabled={saving}
          data-testid="pw-submit"
          className="px-6 py-3 bg-terracotta text-white rounded-full hover:bg-terracotta-hover disabled:opacity-40">
          {saving ? "Speichert…" : "Passwort ändern"}
        </button>
      </div>
    </form>
  );
}

const Field = ({ label, value, onChange, textarea = false, testid }) => (
  <label className="block">
    <span className="text-xs uppercase tracking-widest text-ink-muted">{label}</span>
    {textarea ? (
      <textarea data-testid={testid} value={value || ""} onChange={(e) => onChange(e.target.value)} rows={3} className="mt-2 w-full px-3 py-2 border border-border rounded-md" />
    ) : (
      <input data-testid={testid} value={value || ""} onChange={(e) => onChange(e.target.value)} className="mt-2 w-full px-3 py-2 border border-border rounded-md" />
    )}
  </label>
);
